/*
	Overview of PrimaryTokenServiceWrapper:
	** PrimaryTokenServiceWrapper decides if OIDC(longLivedToken) or DSAuth flow needs to be done based on client configuration
	'longLivedTokenSupport' is enabled or not

	** It fallbacks to DSAuth in case of any failure in OIDC flow (catch block in PrimaryTokenServiceWrapper getPrimaryTokenForURL)

	** The finally block does the required post processing after 'getPrimaryTokenForURL' flow of OIDC or DSAuth has returned. 
	'getPrimaryTokenForURL' flows return with a status object or throws an exception 
	In handleGetPrimaryTokenStatus, if statusObj.shouldClearQueue is true we call clearQueueAndProcessNextItem. (shouldClearQueue will be true for the first request corresponding to a referenceUri
		and inside clearQueueAndProcessNextItem we set statusObj.shouldClearQueue to false for subsequent requests)
	In handleGetPrimaryTokenStatus, statusObj.status can be one of the below: 
		- ERROR_CANCEL_LOGIN : we throw an error so that the request which originated from client gets the error as the response (for first request and remaining requests we throw the error)
		- ERROR_PRIMARY_TOKEN_FETCH_FAILED: this is the default status i.e this will be the case when an exception occurs. 
		we throw an error so that the request which originated from client gets the error as the response. (We throw only for subsequent requests (i.e in the else block) since the first request already threw an exception)
		- PRIMARY_TOKEN_STORED : This means that the first request in the queue is complete (i.e Primary token was successfully fetched and stored in the db), Remaining requests for that store need not fetch the primary token. 
*/

import {
	HTTPFetchResponse,
	QueuedRequest,
	PrimaryTokenStatus,
} from './dsAuth/data/AuthTypes';
import { config } from './AuthConfig';
import OIDCPrimaryTokenService from './oidc/PrimaryTokenService';
import TokenServiceFactory from './dsAuth/TokenServiceFactory';
import { stringConstants, primaryTokenStatusConstants } from './AuthConstants';
import { AotCategory, logConstants } from '../utils/Constants';
import AuthMan from './AuthMan';
import { PrimaryTokenFetchFailure } from './AuthErrors';
import { Logger } from './Logger';
import { SentryLogger } from '../sentryLogging/SentryLogger';
import * as Sentry from '@sentry/browser';
import { StoreType } from './TypeDefinitions';

export class PrimaryTokenServiceWrapper {
	private static instance: PrimaryTokenServiceWrapper;
	static getInstance(): PrimaryTokenServiceWrapper {
		if (!PrimaryTokenServiceWrapper.instance) {
			PrimaryTokenServiceWrapper.instance = new PrimaryTokenServiceWrapper();
		}
		return PrimaryTokenServiceWrapper.instance;
	}

	async getPrimaryTokenForURL(
		response: HTTPFetchResponse,
		referenceUri: string,
		id: string,
		transactionId: string,
		allowSilentLogin: boolean,
		location: string
	): Promise<void> {
		const authMan = AuthMan.getInstance();

		Logger.getInstance().write(logConstants.INFO, id + ': adding request to queue');
		const queuedRequest = new QueuedRequest(id, referenceUri);
		authMan.requestQueue.push(queuedRequest);

		let statusObj = new PrimaryTokenStatus(
			primaryTokenStatusConstants.ERROR_PRIMARY_TOKEN_FETCH_FAILED,
			true
		);

		if (authMan.isAuthInProgress) {
			Logger.getInstance().write(logConstants.INFO, id + ': Auth in progress, waiting');
			const waitStatusObj: PrimaryTokenStatus = await new Promise(
				(resolve: Function, reject: Function) => {
					queuedRequest.waitStatusResolve = resolve;
					queuedRequest.reject = reject;
				}
			);
			Logger.getInstance().write(
				logConstants.INFO,
				id + ': waitStatusResolved: ' + JSON.stringify(waitStatusObj)
			);
			// we need this check because we don't know if this request for the same store as the request that is fetching the primary token
			// if it's for a different store, we need to proceed to get the primary token for this store
			// otherwise we can skip everything and handle the response and go back to fetch secondary token if possible
			if (waitStatusObj.status !== stringConstants.RESUME_PRIMARY_TOKEN_OR_AAAC_FETCH) {
				this.handleGetPrimaryTokenStatus(waitStatusObj, referenceUri, id);
				return;
			}
		}
		//either this was the first request and isAuthInProgress was false, or the request was woken up by clearQueueAndProcessNextItem
		authMan.isAuthInProgress = true;
		try {
			const storeConfig = authMan.getStoreConfiguration();
			if (
				config.longLivedTokenSupport &&
				(!storeConfig[referenceUri]?.storeType ||
					storeConfig[referenceUri]?.storeType === StoreType.Cloud)
			) {
				Logger.getInstance().write(
					logConstants.INFO,
					id + ': Calling OIDC getPrimaryTokenForURL'
				);
				statusObj = await OIDCPrimaryTokenService.getInstance().getPrimaryTokenForURL(
					response,
					referenceUri,
					id,
					transactionId,
					allowSilentLogin
				);
			} else {
				if (
					config.isUnifiedAuthenticationEnabled &&
					storeConfig[referenceUri]?.storeType !== StoreType.Cloud
				) {
					try {
						statusObj = await TokenServiceFactory.getPrimaryTokenServiceInstance(
							referenceUri
						).getPrimaryTokenForURL(response, referenceUri, id, transactionId, location);
					} catch (e) {
						Logger.getInstance().write(
							logConstants.ERROR,
							id + ': Token fetch failed in DSAuth flow :' + e.message,
							AotCategory.Error,
							'Failed to fetch primary token'
						);
					}
				} else {
					throw new Error('LONG_LIVED_TOKEN_SUPPORT_DISABLED');
				}
			}
		} catch (e) {
			const breadcrumb = {
				category: 'PrimaryTokenService',
				message: e.message,
				level: Sentry.Severity.Error,
			};
			SentryLogger.getInstance().addBreadCrumb(breadcrumb);
			Logger.getInstance().write(
				logConstants.ERROR,
				id + ': Falling back to DSAuth flow due to reason :' + e.message
			);

			statusObj = await TokenServiceFactory.getPrimaryTokenServiceInstance(
				referenceUri
			).getPrimaryTokenForURL(response, referenceUri, id, transactionId, location);
		} finally {
			this.handleGetPrimaryTokenStatus(statusObj, referenceUri, id);
		}
	}

	private handleGetPrimaryTokenStatus(
		statusObj: PrimaryTokenStatus,
		referenceUri: string,
		id: string
	): void {
		Logger.getInstance().write(
			logConstants.INFO,
			`${id}:inside handleGetPrimaryTokenStatus with ${JSON.stringify(statusObj)}`
		);
		if (statusObj.shouldClearQueue) {
			setTimeout(() => {
				Logger.getInstance().write(
					logConstants.INFO,
					`${id}:calling clearQueueAndProcessNextItem with ${JSON.stringify(statusObj)}`
				);
				AuthMan.getInstance().clearQueueAndProcessNextItem(referenceUri, statusObj);
			}, 0);
			if (
				statusObj.status == primaryTokenStatusConstants.ERROR_CANCEL_LOGIN ||
				statusObj.status == primaryTokenStatusConstants.ERROR_AUTHDOMAIN_CHANGED ||
				statusObj.status == primaryTokenStatusConstants.ERROR_SILENT_LOGIN_FAILURE
			) {
				throw new PrimaryTokenFetchFailure(new Error(statusObj.status));
			}
		} else {
			if (
				statusObj.status == primaryTokenStatusConstants.ERROR_CANCEL_LOGIN ||
				statusObj.status ==
					primaryTokenStatusConstants.ERROR_PRIMARY_TOKEN_FETCH_FAILED ||
				statusObj.status == primaryTokenStatusConstants.ERROR_AUTHDOMAIN_CHANGED
			) {
				throw new PrimaryTokenFetchFailure(new Error(statusObj.status));
			}
		}
	}
}
